import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Modal, Select, message, Tooltip, Empty } from "antd";
import { RiInformationLine } from "react-icons/ri";
import TemplatePreview from "./TemplatePreview";
import {
  buildTemplateSendComponents,
  collectTemplateInputs,
  extractTemplateFields,
  languageLabel,
  renderTemplateText,
  templateBody,
} from "./TemplateUtils";
import styles from "./templates.module.css";

export default function TemplateSendModal({
  open,
  currentPhone,
  contactName,
  contacts = [],
  messaging,
  onCancel,
  onSent,
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedName, setSelectedName] = useState("");
  const [to, setTo] = useState(currentPhone || "");
  const [searchValue, setSearchValue] = useState("");
  const [values, setValues] = useState({});
  const [previewMode, setPreviewMode] = useState("message");

  useEffect(() => {
    if (!open || !messaging) return;
    setTo(currentPhone || "");
    setPreviewMode("message");
    setLoading(true);
    messaging
      .getTemplates()
      .then((data) => {
        const approved = (data.templates || []).filter(
          (t) => String(t.status).toUpperCase() === "APPROVED"
        );
        setTemplates(approved);
      })
      .catch((err) =>
        message.error(err.response?.data?.message || err.message || "Could not load templates")
      )
      .finally(() => setLoading(false));
  }, [open, messaging, currentPhone]);


  const selected = templates.find((t) => t.name === selectedName);
  const fields = useMemo(
    () => (selected ? extractTemplateFields(selected) : { header: "", body: "", footer: "", buttons: [], category: "" }),
    [selected]
  );
  const templateInputs = useMemo(() => collectTemplateInputs(fields), [fields]);

  useEffect(() => {
    if (!selected) {
      setValues({});
      return;
    }
    setValues((prev) => {
      const next = {};
      templateInputs.forEach(({ id }) => {
        next[id] = prev[id] ?? "";
      });
      return next;
    });
  }, [selected, templateInputs]);

  const renderedHeader = renderTemplateText(
    fields.header,
    Object.fromEntries(
      templateInputs
        .filter((item) => item.scope === "header")
        .map((item) => [item.key, values[item.id]])
    )
  );
  const renderedBody = renderTemplateText(
    fields.body,
    Object.fromEntries(
      templateInputs
        .filter((item) => item.scope === "body")
        .map((item) => [item.key, values[item.id]])
    )
  );

  const send = async () => {
    if (!to.trim()) {
      message.warning("Select a contact or enter a WhatsApp number");
      return;
    }
    if (!selected) {
      message.warning("Select a template");
      return;
    }
    setSending(true);
    try {
      const targetContact = contacts.find((c) => c.phone === to);
      const finalContactName = targetContact?.nameOnly || contactName || "";
      await messaging.sendTemplate(
        to.trim(),
        {
          name: selected.name,
          language_code: selected.language,
          components: buildTemplateSendComponents(fields, values),
          preview_text: [renderedHeader, renderedBody, fields.footer].filter(Boolean).join("\n"),
          template_name: selected.name,
        },
        finalContactName
      );
      message.success("Template sent");
      onSent?.();
    } catch (err) {
      message.error(err.response?.data?.message || err.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      title="Send template"
      open={open}
      onCancel={onCancel}
      width={1000}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button key="send" type="primary" loading={sending} onClick={send}>Send template</Button>,
      ]}
      destroyOnClose
    >
      <div className={styles.sendLayout}>
        <div className={styles.sendPicker}>
          {/* Select Template dropdown */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label className={styles.pickerLabel}>Select Template</label>
            <Tooltip title="Only approved templates can be sent. Templates currently in review are hidden.">
              <RiInformationLine style={{ cursor: 'help', color: '#1890ff' }} />
            </Tooltip>
          </div>
          <Select
            showSearch
            placeholder="Search and select a template"
            value={selectedName || undefined}
            onChange={setSelectedName}
            className={styles.sendSelect}
            options={templates.map((t) => ({
              value: t.name,
              label: `${t.name} - ${languageLabel(t.language)}`,
              searchText: `${t.name} ${t.category} ${templateBody(t)}`.toLowerCase()
            }))}
            filterOption={(input, option) => 
              String(option?.searchText || "").includes(input.toLowerCase().trim())
            }
            loading={loading}
          />

          {/* Recipient number */}
          <label className={styles.pickerLabel}>Recipient</label>
          <Select
            showSearch
            optionLabelProp="value"
            placeholder="WhatsApp number"
            value={to || undefined}
            onChange={setTo}
            onSearch={setSearchValue}
            className={styles.sendSelect}
            options={(() => {
              const list = contacts.map(c => ({
                value: c.phone,
                label: `${c.nameOnly || "Unknown"} (${c.phone})`
              }));
              if (searchValue && !list.find(o => o.value === searchValue)) {
                list.unshift({ value: searchValue, label: `Use "${searchValue}"` });
              }
              return list;
            })()}
            dropdownRender={(menu) => (
              <>
                {menu}
                <div style={{ padding: '8px', borderTop: '1px solid #e8e8e8', textAlign: 'center' }}>
                  <small style={{ color: '#999' }}>Search or type a new number</small>
                </div>
              </>
            )}
          />

          {/* Template Info Card */}
          <div className={styles.templateInfoCard}>
            {selected ? (
              <>
                <div className={styles.templateInfoHeader}>
                  <strong>{selected.name}</strong>
                  <div className={styles.templateInfoMeta}>
                    {selected.category} - {languageLabel(selected.language)}
                  </div>
                </div>
                <div className={styles.variableList}>
                  {templateInputs.length > 0 ? (
                    templateInputs.map((item) => (
                      <div key={item.id} className={styles.variableRow}>
                        <span className={styles.variableKey}>{"{{"}{item.key}{"}}"}</span>
                        <Input
                          value={values[item.id] ?? ""}
                          onChange={(e) => setValues(prev => ({ ...prev, [item.id]: e.target.value }))}
                          placeholder={`Enter ${item.key}`}
                        />
                      </div>
                    ))
                  ) : (
                    <div className={styles.variableEmpty}>This template has no editable variables.</div>
                  )}
                </div>
              </>
            ) : (
              <Empty description="No template selected" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            )}
          </div>
        </div>

        {/* Right side preview */}
        <div className={styles.sendPreviewPanel}>
          <TemplatePreview
            header={renderedHeader}
            body={renderedBody}
            footer={fields.footer}
            buttons={fields.buttons}
            originalHeader={fields.header}
            originalBody={fields.body}
            mode={previewMode}
            onModeChange={setPreviewMode}
          />
        </div>
      </div>
    </Modal>
  );
}
